import { clientConfig } from '@/config/clientConfig';
import { CheckboxMultiSelect } from '@/shared/components/CheckboxMultiSelect';
import { useDebouncedValue } from '@/shared/hooks/useDebouncedValue';
import { useUrlFilters } from '@/shared/hooks/useUrlFilters';
import { showInfoToast, showSuccessToast } from '@/shared/notifications/toast-utils';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Search, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { JOB_LIST_DEFAULTS } from '../constants/jobListDefaults';
import { tabImposesStatus } from '../constants/tabConfig';
import { useWorkOrderJobTypes } from '../hooks/useWorkOrderJobTypes';
import { useWorkOrderPriorities } from '../hooks/useWorkOrderPriorities';
import { useWorkOrderStatusList } from '../hooks/useWorkOrderStatusList';

export function JobListFilters() {
  const [filters, setFilters] = useUrlFilters(JOB_LIST_DEFAULTS);
  const { data: jobTypesData } = useWorkOrderJobTypes();
  const { data: prioritiesData, isLoading: prioritiesLoading } = useWorkOrderPriorities();
  const { data: statusData, isLoading: statusesLoading } = useWorkOrderStatusList();

  const selectedPriorities =
    filters.priority_id === 'all' ? [] : filters.priority_id.split(',').filter(Boolean);

  const selectedStatuses =
    filters.work_order_status_id === 'all'
      ? []
      : filters.work_order_status_id.split(',').filter(Boolean);

  // The status tabs (New Orders, Accepted, Validated…) already constrain the
  // list to specific statuses, so the Status filter is only offered where the
  // tab imposes none — the "All" tab and the MIR "More Info Requested" tab.
  const showStatusFilter = !tabImposesStatus(filters.tab);

  const [searchInput, setSearchInput] = useState(filters.search_term);
  const debouncedSearch = useDebouncedValue(searchInput, 300);

  // Sync local input when filters change externally.
  useEffect(() => {
    setSearchInput(filters.search_term);
  }, [filters.search_term]);

  // Push search to URL once the debounce settles — the searchInput check avoids
  // re-applying a stale value right after the input was cleared.
  useEffect(() => {
    if (debouncedSearch === searchInput && debouncedSearch !== filters.search_term) {
      setFilters({ search_term: debouncedSearch, page: '1' });
    }
  }, [debouncedSearch, searchInput, filters.search_term, setFilters]);

  const handleSearchChange = (value: string) => setSearchInput(value);

  const handlePriorityChange = (next: string[]) => {
    setFilters({ priority_id: next.length > 0 ? next.join(',') : 'all', page: '1' });
    if (next.length === 0) {
      showInfoToast('Priority filter cleared');
      return;
    }
    const names = next
      .map((id) => prioritiesData?.find((p) => String(p.id) === id)?.name ?? id)
      .join(', ');
    showSuccessToast('Filters applied', `Priority: ${names}`);
  };

  const handleStatusChange = (next: string[]) => {
    setFilters({
      work_order_status_id: next.length > 0 ? next.join(',') : 'all',
      page: '1',
    });
    if (next.length === 0) {
      showInfoToast('Status filter cleared');
      return;
    }
    const names = next
      .map((id) => statusData?.find((s) => String(s.id) === id)?.name ?? id)
      .join(', ');
    showSuccessToast('Filters applied', `Status: ${names}`);
  };

  const handleJobTypeChange = (value: string) => {
    setFilters({ job_category_id: value, page: '1' });
    if (value === 'all') {
      showInfoToast('Job Type filter cleared');
      return;
    }
    const name = jobTypesData?.find((j) => String(j.id) === value)?.name ?? value;
    showSuccessToast('Filters applied', `Job Type: ${name}`);
  };

  const handlePsrChange = (value: string) => {
    setFilters({ psr: value, page: '1' });
  };

  const handleReset = () => {
    setSearchInput('');
    setFilters({
      priority_id: 'all',
      work_order_status_id: 'all',
      job_category_id: 'all',
      psr: 'all',
      search_term: '',
      page: '1',
    });
    showSuccessToast('Filters cleared');
  };

  const hasActiveFilters =
    filters.priority_id !== 'all' ||
    // A stale status selection only counts as active while the filter is shown;
    // on a status-defined tab the control is hidden and its value is ignored.
    (showStatusFilter && filters.work_order_status_id !== 'all') ||
    filters.job_category_id !== 'all' ||
    filters.psr !== 'all' ||
    filters.search_term !== '';

  return (
    <div className="flex items-center gap-3 flex-wrap">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search jobs..."
          value={searchInput}
          onChange={(e) => handleSearchChange(e.target.value)}
          className="pl-9 h-10 w-[260px]"
        />
      </div>

      <CheckboxMultiSelect
        options={prioritiesData}
        isLoading={prioritiesLoading}
        selected={selectedPriorities}
        onChange={handlePriorityChange}
        allLabel="All Priorities"
      />

      {showStatusFilter && (
        <CheckboxMultiSelect
          options={statusData}
          isLoading={statusesLoading}
          selected={selectedStatuses}
          onChange={handleStatusChange}
          allLabel="All Statuses"
          className="h-10 w-[160px]"
        />
      )}

      <Select value={filters.job_category_id} onValueChange={handleJobTypeChange}>
        <SelectTrigger className="h-10 w-[160px]">
          <SelectValue placeholder="All Job Types" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Job Types</SelectItem>
          {jobTypesData?.map((type) => (
            <SelectItem key={type.id} value={String(type.id)}>
              {type.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* The PSR abbreviation isn't used by every brand, so this filter is
          feature-flagged off per brand (SC-6249 — hidden for YW). */}
      {clientConfig.features.psrFilter && (
        <Select value={filters.psr} onValueChange={handlePsrChange}>
          <SelectTrigger className="h-10 w-[150px]">
            {/* Default ('all') shows the filter name as a label; only the active
                state reads as a selection. */}
            <span className="truncate">{filters.psr === 'true' ? 'PSR Only' : 'PSR'}</span>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All jobs</SelectItem>
            <SelectItem value="true">PSR only</SelectItem>
          </SelectContent>
        </Select>
      )}

      {hasActiveFilters && (
        <Button
          variant="ghost"
          onClick={handleReset}
          className="h-10 gap-1.5 text-muted-foreground"
        >
          <X className="w-4 h-4" />
          Clear
        </Button>
      )}
    </div>
  );
}
